/** Extensions rendered as text/markdown documents (a doc icon, not generic file). */
const TEXT_LIKE_EXTENSIONS = new Set([
  'md',
  'markdown',
  'mdown',
  'mkd',
  'mdx',
  'mdwn',
  'mkdn',
  'markdn',
  'txt',
  'text',
]);

/** Markdown extensions — these files open with all three views. */
const MARKDOWN_EXTENSIONS = new Set([
  'md',
  'markdown',
  'mdown',
  'mkd',
  'mdx',
  'mdwn',
  'mkdn',
  'markdn',
]);

/** Image extensions — these files open read-only in the image viewer. */
const IMAGE_EXTENSIONS = new Set([
  'png',
  'jpg',
  'jpeg',
  'gif',
  'webp',
  'svg',
  'bmp',
  'ico',
  'avif',
]);

/** Extension → MIME type for the image kinds we view. */
const IMAGE_MIME_TYPES: Record<string, string> = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
  svg: 'image/svg+xml',
  bmp: 'image/bmp',
  ico: 'image/x-icon',
  avif: 'image/avif',
};

/** The lowercase extension of `name` (without the dot), or '' if none. */
function extOf(name: string): string {
  const dot = name.lastIndexOf('.');
  if (dot < 0 || dot === name.length - 1) return '';
  return name.slice(dot + 1).toLowerCase();
}

/** True when `name` looks like a plain-text or markdown document by extension. */
export function isTextLikeFile(name: string): boolean {
  const dot = name.lastIndexOf('.');
  if (dot < 0 || dot === name.length - 1) return false;
  return TEXT_LIKE_EXTENSIONS.has(name.slice(dot + 1).toLowerCase());
}

/**
 * True when `name` is a Markdown file by extension. Markdown files get the full
 * three-view experience; everything else is treated as a code/plain-text file
 * (plain-text editor only). Untitled buffers (no name) are handled by the caller.
 */
export function isMarkdownFile(name: string): boolean {
  const dot = name.lastIndexOf('.');
  if (dot < 0 || dot === name.length - 1) return false;
  return MARKDOWN_EXTENSIONS.has(name.slice(dot + 1).toLowerCase());
}

/**
 * True when `name` is an image we can view by extension (png, jpg, jpeg, gif,
 * webp, svg, bmp, ico, avif). Image files open in a read-only viewer.
 */
export function isImageFile(name: string): boolean {
  return IMAGE_EXTENSIONS.has(extOf(name));
}

/**
 * MIME type for an image `name` (used to build the viewer's blob). Falls back to
 * the generic `application/octet-stream` for unknown extensions — callers should
 * gate on `isImageFile` first.
 */
export function imageMimeType(name: string): string {
  return IMAGE_MIME_TYPES[extOf(name)] ?? 'application/octet-stream';
}

/**
 * Extensions we can't meaningfully open (binary / unsupported) — shown greyed
 * out in the tree. Images are NOT here (they open in the viewer). PDF is here
 * for now (viewer deferred). Everything else — text, code, data, extensionless
 * files like README/LICENSE/Makefile — is treated as openable text.
 */
const UNREADABLE_EXTENSIONS = new Set([
  // documents (no viewer yet)
  'pdf',
  'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'odt', 'ods', 'odp', 'rtf',
  // archives
  'zip', 'gz', 'tgz', 'tar', 'bz2', 'xz', '7z', 'rar',
  // executables / binaries / libs
  'exe', 'dll', 'so', 'dylib', 'dmg', 'pkg', 'app', 'msi', 'deb', 'rpm',
  'bin', 'dat', 'o', 'a', 'class', 'jar', 'war', 'wasm', 'node',
  // audio / video
  'mp3', 'wav', 'flac', 'aac', 'ogg', 'm4a', 'mp4', 'mov', 'avi', 'mkv', 'webm', 'wmv',
  // fonts
  'woff', 'woff2', 'ttf', 'otf', 'eot',
  // design / databases
  'psd', 'ai', 'sketch', 'fig', 'db', 'sqlite', 'sqlite3',
]);

/**
 * True when the app can open + display `name`: any image, any extensionless
 * file (assumed text), or any extension that isn't a known binary/unsupported
 * type. Unreadable files are greyed out + non-openable in the tree.
 */
export function isReadableFile(name: string): boolean {
  if (isImageFile(name)) return true;
  const ext = extOf(name);
  if (ext === '') return true;
  return !UNREADABLE_EXTENSIONS.has(ext);
}
